import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment.prod';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ToastService } from './toast.service';
import { Router } from '@angular/router';
import { TourService } from './tour.service';
import { OrgPlanService } from './org-plan.service';
// import { VerifyOtpResponse } from 'src/app/modules/Authentication/user-application/user-application.component';
export interface Users {
  name: string;
  password: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private currentUserSubject: BehaviorSubject<any>;
  public currentUser: Observable<any>;
  sendotpemail: any;

  constructor(
    private http: HttpClient,
    private toast: ToastService,
    private router: Router,
    private tourService: TourService,
    private orgPlanService: OrgPlanService,
  ) {
    this.currentUserSubject = new BehaviorSubject<any>(
      localStorage.getItem('user')
        ? JSON.parse(localStorage.getItem('user') || 'null')
        : null,
    );
    this.currentUser = this.currentUserSubject.asObservable();
  }

  public get currentUserValue() {
    return this.currentUserSubject.value;
  }

  /** Signed in = a saved user AND a token in storage. Use this (not just
   *  currentUserValue) to decide what a signed-in user may see. */
  get isLoggedIn(): boolean {
    return !!this.currentUserSubject.value && !!localStorage.getItem('access_token');
  }

  /**
   * Storage can change under the app (DevTools "clear storage", another tab
   * logging out). If the token is gone but a user is still remembered in
   * memory, forget that user. Returns true when a session was dropped.
   */
  syncWithStorage(): boolean {
    if (this.currentUserSubject.value && !localStorage.getItem('access_token')) {
      this.currentUserSubject.next(null);
      return true;
    }
    return false;
  }

  private expiringSession = false;

  /**
   * Automatic logout (token expired / invalid / removed). The ONLY place the
   * "session expired" message is shown — once, even if several requests fail
   * together — then the user lands on their own login page.
   */
  expireSession(message = 'Your session has expired. Please login again.'): void {
    if (this.expiringSession) return;
    this.expiringSession = true;

    const loginPage = this.loginPageForStoredUser();
    const returnUrl = this.router.url;

    this.toast.showError(message);
    this.logout(false, loginPage, returnUrl);

    setTimeout(() => (this.expiringSession = false), 3000);
  }

  /**
   * Default landing page for the signed-in user's role — the same routing
   * the client login uses right after sign-in. Returns null when the role is
   * unknown, so callers can fall back safely instead of looping on '/'.
   */
  roleHomeUrl(): string | null {
    const readJson = (key: string): any => {
      try {
        return JSON.parse(localStorage.getItem(key) || 'null');
      } catch {
        return null;
      }
    };
    const user = readJson('user') || {};
    const currentUser = readJson('currentUser') || {};
    const role = String(
      user.role_name || user.role || localStorage.getItem('role_name') ||
      localStorage.getItem('role') || currentUser.role || currentUser.user?.role_name || '',
    )
      .toLowerCase()
      .replace(/\s/g, '_');

    switch (role) {
      case 'system_admin':
        return '/system-admin-dashboard';
      case 'org_admin':
      case 'hr_head':
      case 'hr':
        return '/hr-dashboard';
      case 'tl':
      case 'team_lead':
        return '/tl-dashboard';
      case 'recruiter':
        return '/recruiter-dashboard';
      case 'reporting_manager':
        return '/reporting-manager-dashboard';
      case 'employee':
        return '/lms/employee-learning';
      case 'cnd': {
        // Candidate who signed in through an organization's job post → exam only.
        const org = readJson('org');
        return org?.id ? '/exam' : '/candidate-profile';
      }
      case 'freelancer':
      case 'candidate':
        return '/candidate-assesment/start-assesment';
      default:
        return null;
    }
  }

  /** Each role's normal login page (read before storage is cleared). */
  private loginPageForStoredUser(): string {
    let role = '';
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}') || {};
      role = String(user.role_name || user.role || localStorage.getItem('role_name') || '');
    } catch {
      role = String(localStorage.getItem('role_name') || '');
    }
    if (!role) {
      role = String(this.currentUserSubject.value?.role_name || this.currentUserSubject.value?.role || '');
    }
    role = role.toLowerCase().replace(/\s/g, '_');
    if (role === 'system_admin') return '/auth/sign-in';
    if (['cnd', 'candidate', 'freelancer'].includes(role)) return '/auth/sign-in';
    return '/auth/login';
  }

  getUserRole(): string {
    const user = this.currentUserValue.user;
    return user ? user.role : '';
  }

  login(data: Users) {
    return this.http
      .post<any>(`${environment._url}/api/user/api/system-admin/login/`, data)
      .pipe(
        map((response) => {
          // Check if the response contains required fields
          if (
            response &&
            response.access &&
            response.refresh &&
            response.role
          ) {
            // Store user details and tokens in local storage
            localStorage.setItem('access_token', response.access);
            localStorage.setItem('refresh_token', response.refresh);
            localStorage.setItem('user_id', response.user_id.toString());
            localStorage.setItem('username', response.username);
            localStorage.setItem('email', response.email);
            localStorage.setItem('role', response.role);

            // Emit the user as the current logged-in user
            this.currentUserSubject.next(response.user);
            localStorage.setItem('user', JSON.stringify(response.user));

            // Redirect based on role

            this.router.navigate(['/org-management']);

            return response;
          } else {
            throw new Error('Invalid login response structure');
          }
        }),
        // Optionally handle errors here
      );
  }

  clientLogin(data: Users) {
    return this.http.post<any>(`${environment._url}/user/login/`, data).pipe(
      map((response) => {
        // ✅ FIXED CONDITION
        if (response && response.access && response.refresh && response.user) {
          // ✅ STORE TOKENS
          localStorage.setItem('access_token', response.access);
          localStorage.setItem('refresh_token', response.refresh);

          // ✅ STORE USER OBJECT (BEST PRACTICE)
          localStorage.setItem('user', JSON.stringify(response.user));

          // OPTIONAL individual fields
          localStorage.setItem('user_id', response.user.id.toString());
          localStorage.setItem('username', response.user.username);
          localStorage.setItem('email', response.user.email);
          localStorage.setItem('role', response.user.role);
          localStorage.setItem('role_name', response.user.role_name);
          localStorage.setItem('organization_id', response.user.organization);
          localStorage.setItem(
            'organization_name',
            response.user.organization_name,
          );

          // The organization a candidate signed in through arrives at the
          // ROOT of the login response, not on `user` — `user.organization`
          // is null for both a self-registered candidate and one who applied
          // to an org's job post, so `response.org` is the only thing that
          // tells the two apart. Stored whole so the header and the
          // post-login redirect can both read it back.
          localStorage.setItem('org', JSON.stringify(response.org ?? null));

          // Employee's HR Head ({ id, name }) — root of the login response.
          // Used (with user.reporting_manager_name) as the signatories on an
          // employee's LMS course certificate.
          localStorage.setItem('hr_head', JSON.stringify(response.hr_head ?? null));

          // Tells the header's trial countdown chip (and any billed-action
          // screen) whether this org is still inside its free trial, per
          // the login response's `organization_plan` block.
          this.orgPlanService.setOrgPlan(response.organization_plan ?? null);

          // ✅ Emit current user
          this.currentUserSubject.next(response.user);

          return response;
        } else {
          throw new Error('Invalid login response structure');
        }
      }),
    );
  }

  empLogin(data: Users) {
    return this.http
      .post<any>(`${environment._url}/api/user/api/login/`, data)
      .pipe(
        map((response) => {
          if (
            response &&
            response.access &&
            response.refresh &&
            response.role
          ) {
            // Store tokens and user info in localStorage
            localStorage.setItem('access_token', response.access);
            localStorage.setItem('refresh_token', response.refresh);
            localStorage.setItem('user_id', response.user_id.toString());
            localStorage.setItem('username', response.username);
            localStorage.setItem('email', response.email);
            localStorage.setItem('role', response.role);
            localStorage.setItem('is_bot_round', response.is_bot_round);
            localStorage.setItem('is_accepted', response.is_accepted); // ✅ Store for reference
            localStorage.setItem('organization_id', response.organization_id);
            localStorage.setItem(
              'organization_name',
              response.organization_name,
            );
            localStorage.setItem('exam_completed', response.exam_completed);
            localStorage.setItem(
              'role_name',
              response.role_name || response.role,
            );

            // Employee's HR Head ({ id, name }) for the LMS certificate
            // signatories — same as clientLogin(); harmless when absent.
            localStorage.setItem('hr_head', JSON.stringify(response.hr_head ?? null));

            // Persist the user object too — the header menu and the auth guard
            // both read `user` from localStorage, so without this an employee
            // or candidate loses their navigation on every page refresh.
            localStorage.setItem(
              'user',
              JSON.stringify({
                id: response.user_id,
                username: response.username,
                email: response.email,
                role: response.role,
                role_name: response.role_name || response.role,
                organization: response.organization_id,
                organization_name: response.organization_name,
              }),
            );

            // Store assignment ID for candidates
            if (
              response.role.toLowerCase() === 'candidate' &&
              response.assignment_id
            ) {
              localStorage.setItem(
                'assignment_id',
                response.assignment_id.toString(),
              );
              localStorage.setItem('exam_id', response.exam_id.toString());
              localStorage.setItem('is_slot_booked', response.is_slot_booked);
            }

            // Emit current user
            this.currentUserSubject.next({
              user_id: response.user_id,
              username: response.username,
              email: response.email,
              role: response.role,
              is_bot_round: response.is_bot_round,
              organization_id: response.organization_id,
              organization_name: response.organization_name,
            });

            const role = response.role.toLowerCase();

            // ✅ Redirect logic
            if (response.role.toLowerCase() === 'candidate') {
              // 🔴 If candidate has already accepted the offer, send to confirmation page
              if (response.is_accepted === true) {
                this.router.navigate(['/acceptorreject-offer']);
              } else if (response.is_bot_round && response.is_slot_booked) {
                this.router.navigate(['/ai-interview']);
              } else if (response.is_bot_round && !response.is_slot_booked) {
                this.router.navigate(['/book-slot']);
              } else {
                this.router.navigate(['/candidate-assesment/start-assesment']);
              }
            } else if (['tl', 'team_lead'].includes(role)) {
              // TLs land on their recruitment overview first.
              this.router.navigate(['/employee-dashboard']);
            } else if (role === 'recruiter') {
              this.router.navigate(['/recruiter-dashboard']);
            } else if (role === 'hr') {
              // HR Head lands on their org-wide executive briefing.
              this.router.navigate(['/hr-dashboard']);
            } else if (role === 'reporting_manager') {
              this.router.navigate(['/candidate-dashboard']);
            } else if (role === 'employee') {
              this.router.navigate(['/lms/employee-learning']);
            } else {
              this.router.navigate(['/candidate-dashboard']);
            }

            return response;
          } else {
            throw new Error('Invalid login response structure');
          }
        }),
      );
  }

  register(data: any): Observable<any> {
    return this.http.post<any>(`${environment._url}/user/register/`, data);
  }

  sendOtp(data: any): Observable<any> {
    return this.http.post<any>(`${environment._url}/user/register/`, data);
  }

  registerEmployee(data: any) {
    return this.http.post<any>(
      `${environment._url}/api/user/role-user/register/`,
      data,
    );
  }

  /**
   * @param showLogoutToast  "Signed out successfully" (manual logout only).
   * @param redirectTo       Login page to land on (auto-logout picks the
   *                         user's own login page; manual logout keeps the
   *                         previous destination).
   * @param returnUrl        Page to come back to after signing in again.
   */
  logout(showLogoutToast: boolean = true, redirectTo?: string, returnUrl?: string): void {
    // One login path can store the string "undefined" under 'user', which
    // makes a bare JSON.parse throw and abort the whole logout.
    let role: string | undefined;

    try {
      const raw = localStorage.getItem('user');

      role =
        raw && raw !== 'undefined' && raw !== 'null'
          ? JSON.parse(raw)?.role_name
          : undefined;
    } catch {
      role = undefined;
    }

    // Product tours are "first visit, then every couple of months" - they are
    // not session state, so they must outlive the wipe. The interceptor calls
    // logout(false) on any 401, which would otherwise reset them silently.
    const tourFlags = this.tourService.snapshotTours();

    localStorage.clear();

    sessionStorage.clear();

    this.tourService.restoreTours(tourFlags);

    this.currentUserSubject.next(null);

    // '/auth/sign-in' is where the old '/sign-in' ended up (via the '**'
    // fallback) — same page, without the detour.
    const redirectUrl = redirectTo || '/auth/sign-in';
    const keepReturnUrl = returnUrl && !returnUrl.startsWith('/auth') && returnUrl !== '/';

    this.router.navigate([redirectUrl], keepReturnUrl ? { queryParams: { returnUrl } } : {});

    if (showLogoutToast) {
      this.toast.showSuccess('Signed out successfully');
    }
  }
  forgotPassword(payload: { email: string }): Observable<any> {
    return this.http.post<any>(
      `${environment._url}/api/user/forgot-password/`,
      payload,
    );
  }

  resetPasswordWithToken(data: any): Observable<any> {
    return this.http.post<any>(
      `${environment._url}/api/user/reset-password/`,
      data,
    );
  }

  getRoleTypes(): Observable<any> {
    return this.http.get<any>(`${environment._url}/user/masters/roles/`);
  }

  uploadCV(data: any): Observable<any> {
    return this.http.post<any>(`${environment._url}/jp/upload-cv/`, data);
  }

  uploadexcel(data: any): Observable<any> {
    return this.http.post<any>(`${environment._url}/jp/upload_post_job/`, data);
  }

  uploadprofile(id: any, data: any): Observable<any> {
    let url = '/jp/personal_details/' + id + '/update_profile_pic/';

    return this.http.patch<any>(`${environment._url + url}`, data);
  }

  sendotp(data: any): Observable<any> {
    return this.http.post<any>(
      `${environment._url}/api/candidate/send-otp/`,
      data,
    );
  }

  verifyOtp(data: any): Observable<any> {
    return this.http.post(
      `${environment._url}/user/verify-otp/`,
      data,
    );
  }

  resetPassword(data: any): Observable<any> {
    return this.http.post<any>(
      `${environment._url}/test/reset-password/`,
      data,
    );
  }

  getPosts(orgId: any): Observable<any> {
    return this.http.get<any>(`${environment._url}/jobpost/post-jobs/`);
  }

  cndRegister(data: any) {
    return this.http.post(`${environment._url}/api/candidate/register/`, data);
  }

  salesCndRegister(data: any) {
    return this.http.post(
      `${environment._url}/api/candidate/sales_candidate/register/`,
      data,
    );
  }
}
